'use client';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Navbar from '@/components/Navbar';
import AboutSection from '@/components/AboutSection';
import Footer from '@/components/Footer';
import AirportSearchInput from '@/components/AirportSearchInput';
import BlogArticlesPreview from '@/components/BlogArticlesPreview';
import FaqAccordion from '@/components/FaqAccordion';
import { Plane, MapPin, Star, Shield, Clock, ArrowRight, Globe, Phone, X, CheckCircle, Building2, ChevronRight, AlertCircle, ChevronDown } from 'lucide-react';

const HOME_INTRO_PARAGRAPHS = [
  'Se procura uma forma simples e acessível de organizar a sua viagem, a Voosworld coloca à sua disposição uma equipa de profissionais dispostos a ajudá-lo em cada etapa do processo. Quer precise de reservar um voo, encontrar uma oferta especial ou resolver qualquer dúvida relacionada com o seu itinerário, os nossos consultores estão preparados para lhe oferecer um atendimento personalizado.',
  'Além de oferecer tarifas competitivas em passagens aéreas, você também pode planejar toda a sua viagem de um só lugar. Temos pacotes de férias, opções de aluguel de carros e reservas de hotéis para que você tenha uma experiência mais confortável e organizada. Desta forma, terá todos os serviços de viagem reunidos em um mesmo itinerário, o que lhe permitirá economizar tempo e planejar com mais tranquilidade.',
  'Nossa equipe de atendimento ao cliente está disponível o dia todo e a semana toda para ajudá-lo sempre que precisar. Seja antes, durante ou depois da sua reserva, você pode contar com o apoio de nossos especialistas.',
  'Não espere mais para realizar a viagem que tanto deseja. Reserve os seus voos, alojamento, aluguer de carro ou pacotes turísticos com a Voosworld e desfrute de uma experiência de viagem prática, segura e adaptada ao seu orçamento enquanto descobre novos destinos e cria memórias inesquecíveis.',
];

const FEATURES = [
  {
    icon: Shield,
    title: 'Preços econômicos',
    desc: 'Reserve suas passagens por valores baixos e aproveite ofertas acessíveis para viajar mais pagando menos.'
  },
  {
    icon: Clock,
    title: 'Suporte 24 horas por dia, 7 dias por semana',
    desc: 'Nossa equipe está disponível a qualquer momento para oferecer ajuda rápida sempre que você precisar.'
  },
  {
    icon: Globe,
    title: 'Reserva rápida e fácil',
    desc: 'Compre suas passagens de forma simples e rápida, sem complicações.'
  },
  {
    icon: Star,
    title: 'O guia turístico',
    desc: 'Nosso guia experiente irá ajudá-lo com seus problemas e guiá-lo melhor.'
  },
];

export default function HomeClient() {
  const router = useRouter();
  const [tripType, setTripType] = useState('Round Trip');
  const [homeLocations, setHomeLocations] = useState([]);
  const [latestBlog, setLatestBlog] = useState(null);
  const [latestArticle, setLatestArticle] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [introExpanded, setIntroExpanded] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState({
    from: '',
    to: '',
    departure: '',
    returnDate: '',
    passengers: '1 Adult',
    cabinClass: 'Economy',
  });

  function handleField(k, v) { setForm(f => ({ ...f, [k]: v })); if (validationError) setValidationError(''); }

  useEffect(() => {
    fetch('/api/locations').then(r => r.json()).then(d => setHomeLocations(d.slice(0, 6))).catch(() => {});
    fetch('/api/latest-posts').then(r => r.json()).then(d => { setLatestBlog(d.latestBlog); setLatestArticle(d.latestArticle); }).catch(() => {});
    fetch('/api/faqs').then(r => r.json()).then(d => setFaqs(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  function handleSearch() {
    if (!form.from.trim()) { setValidationError('Por favor, informe sua cidade ou aeroporto de partida.'); return; }
    if (!form.to.trim()) { setValidationError('Por favor, informe sua cidade ou aeroporto de destino.'); return; }
    if (!form.departure) { setValidationError('Por favor, selecione uma data de partida.'); return; }
    if (tripType === 'Round Trip' && !form.returnDate) { setValidationError('Por favor, selecione uma data de retorno para sua viagem de ida e volta.'); return; }
    setValidationError('');
    const q = new URLSearchParams({
      from: form.from,
      to: form.to,
      departure: form.departure,
      return: form.returnDate,
      passengers: form.passengers,
      class: form.cabinClass,
      trip: tripType,
    });
    router.push(`/voos?${q.toString()}`);
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative flex items-center justify-center bg-slate-900">
        <img
          src="/images/hero-flight-sky.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-linear-to-b from-slate-900/60 via-slate-900/30 to-slate-900" />
        <div className="absolute top-32 left-12 opacity-20 animate-bounce" style={{ animationDuration: '3s' }}>
          <Plane size={28} className="text-white -rotate-12" />
        </div>
        <div className="absolute top-48 right-20 opacity-15 animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>
          <Plane size={18} className="text-sky-400 rotate-12" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto py-10 lg:py-16">
          <div className="inline-flex items-center gap-2 bg-sky-500/20 border border-sky-500/30 text-sky-300 text-sm px-4 py-2 rounded-full mb-6 backdrop-blur-sm">
            <span className="w-2 h-2 bg-sky-400 rounded-full animate-pulse" />
            A jornada começa com a Voosworld
          </div>
          <h1 className="font-display text-5xl md:text-5xl font-bold text-white mb-6 leading-none">
            Viaje pelo mundo com confiança e <span className="text-sky-400"> especialistas ao seu lado</span>
          </h1>
          <p className="text-slate-300 text-lg md:text-xl mx-auto mb-12 leading-relaxed lg:px-24">
            Explore destinos incríveis, mergulhe em novas culturas e crie memórias únicas que vão transformar sua forma de ver o mundo.
          </p>

          {/* Search Box */}
          <div id="search" className="bg-white rounded-2xl p-4 md:p-6 shadow-2xl max-w-4xl mx-auto">
            <div className="flex gap-4 mb-5 border-b border-slate-100 pb-4">
              {['One Way', 'Round Trip'].map((t) => (
                <button key={t} onClick={() => setTripType(t)} className={`text-sm font-medium px-3 py-1 rounded-full transition-colors ${tripType === t ? 'bg-sky-500 text-white' : 'text-slate-500 hover:text-slate-800'}`}>
                  {t === 'One Way' ? 'Só Ida' : 'Ida e Volta'}
                </button>
              ))}
            </div>

            {/* Validation error popup */}
            {validationError && (
              <div className="mb-4 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                <AlertCircle size={18} className="flex-shrink-0 text-red-500" />
                <span className="flex-1">{validationError}</span>
                <button onClick={() => setValidationError('')} aria-label="Dispensar erro" className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0">
                  <X size={16} />
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <AirportSearchInput
                label="Origem"
                value={form.from}
                onChange={v => handleField('from', v)}
                placeholder="Atlanta (ATL)"
              />
              <AirportSearchInput
                label="Destino"
                value={form.to}
                onChange={v => handleField('to', v)}
                placeholder="Para onde você vai?"
              />
              <div>
                <label htmlFor="search-departure" className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1 text-start">Partida</label>
                <input id="search-departure" type="date" className="w-full px-3 py-3 rounded-xl border border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-300 text-sm" value={form.departure} onChange={e => handleField('departure', e.target.value)} />
              </div>
              <div>
                <label htmlFor="search-return" className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1 text-start">Retorno</label>
                <input id="search-return" type="date" className={`w-full px-3 py-3 rounded-xl border text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-300 text-sm transition-colors ${tripType === 'One Way' ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed' : 'border-slate-200'}`}
                  value={form.returnDate} onChange={e => handleField('returnDate', e.target.value)} disabled={tripType === 'One Way'} />
              </div>
            </div>
            <div className="flex gap-3 mt-4 items-end">
              <div className="flex-1">
                <label htmlFor="search-passengers" className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1 text-start">Passageiros</label>
                <select id="search-passengers" className="w-full px-3 py-3 rounded-xl border border-slate-200 text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-300 text-sm" value={form.passengers} onChange={e => handleField('passengers', e.target.value)}>
                  <option>1 Adulto</option><option>2 Adultos</option><option>2 Adultos, 1 Criança</option><option>2 Adultos, 2 Crianças</option>
                </select>
              </div>
              <div className="flex-1">
                <label htmlFor="search-cabin-class" className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1 text-start">Classe</label>
                <select id="search-cabin-class" className="w-full px-3 py-3 rounded-xl border border-slate-200 text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-300 text-sm" value={form.cabinClass} onChange={e => handleField('cabinClass', e.target.value)}>
                  <option>Econômica</option><option>Econômica Premium</option><option>Executiva</option><option>Primeira Classe</option>
                </select>
              </div>
              <button onClick={handleSearch} className="bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-3 rounded-xl transition-colors flex items-center gap-2 whitespace-nowrap">
                <Plane size={18} /> Buscar Voos
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-sky-500 text-white py-6">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[['500+', 'Companhias aéreas'], ['120+', 'Países'], ['100K+', 'Viajantes satisfeitos'], ['$50', 'Economia média']].map(([n, l]) => (
            <div key={l}><div className="text-3xl font-display font-bold">{n}</div><div className="text-sky-100 text-sm mt-1">{l}</div></div>
          ))}
        </div>
      </section>

      {/* Intro content */}
      <section className="py-14 px-6 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            Quais São As Vantagens de Reservar o Bilhete Com a Voosworld?
          </h2>
          <div className="text-slate-600 text-base md:text-lg leading-relaxed space-y-4">
            <p>{HOME_INTRO_PARAGRAPHS[0]}</p>
            {introExpanded && HOME_INTRO_PARAGRAPHS.slice(1).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          {HOME_INTRO_PARAGRAPHS.length > 1 && (
            <button
              type="button"
              onClick={() => setIntroExpanded(v => !v)}
              aria-expanded={introExpanded}
              className="mt-3 inline-flex items-center gap-1.5 text-sky-600 hover:text-sky-700 font-semibold text-sm transition-colors"
            >
              {introExpanded ? 'Ler menos' : 'Ler mais'}
              <ChevronDown size={16} className={`transition-transform ${introExpanded ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      </section>

      {/* About Section */}
      <AboutSection />

      {/* Features */}
      <section className="py-10 px-4 lg:py-20 lg:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-500 font-semibold text-sm tracking-widest uppercase mb-2">
              Vantagens
            </p>

            <h2 className="font-display text-4xl font-bold text-slate-900">
              Por Que Escolher a Voosworld?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="text-center px-6 py-7 shadow-md rounded-[20px] border border-gray-100">
                <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon size={26} className="text-sky-500" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2 text-[20px]">{title}</h3>
                <p className="text-slate-500 text-md leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust banner */}
      <section className="py-14 px-6 bg-sky-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl font-bold text-white mb-3">
            Viagens Seguras e Confiáveis
          </h2>
          <p className="text-sky-100 text-lg leading-relaxed">
            Reserve seu voo com a Voosworld com total confiança: somos um site seguro, escolhido por milhares de passageiros.
          </p>
        </div>
      </section>

      {/* Blog & Articles Preview */}
      <BlogArticlesPreview latestBlog={latestBlog} latestArticle={latestArticle} />

      {/* Global FAQs — shown just above the footer */}
      <div className='faqs-home'>
          <FaqAccordion faqs={faqs} />
      </div>

      {/* LARGE FOOTER */}
      <Footer />
    </div>
  );
}
